import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  showDetails: boolean;
}

/**
 * Error boundary component for fault tolerance.
 *
 * Catches rendering errors in child components and shows a friendly
 * error page with retry button. Logs error details in development.
 *
 * Features:
 * - Friendly error message with icon
 * - Retry button to reload the page
 * - Expandable error details (dev-friendly)
 * - Accessible with role="alert"
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, showDetails: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Future: send to error reporting service
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo); // dev-only
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, showDetails: false });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  toggleDetails = (): void => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className={styles.container} role="alert">
          <AlertCircle size={48} className={styles.icon} aria-hidden="true" />
          <h2 className={styles.title}>Algo deu errado</h2>
          <p className={styles.message}>
            Ocorreu um erro inesperado. Tente novamente ou recarregue a página.
          </p>
          <button
            type="button"
            className={styles.retryButton}
            onClick={this.handleRetry}
          >
            <RefreshCw size={18} aria-hidden="true" />
            Tentar novamente
          </button>
          {this.state.error && (
            <div className={styles.details}>
              <button
                type="button"
                className={styles.detailsToggle}
                onClick={this.toggleDetails}
                aria-expanded={this.state.showDetails}
              >
                {this.state.showDetails ? 'Ocultar detalhes' : 'Ver detalhes do erro'}
              </button>
              {this.state.showDetails && (
                <pre className={styles.errorText}>
                  {this.state.error.message}
                  {this.state.error.stack ? `\n\n${this.state.error.stack}` : ''}
                </pre>
              )}
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
